import React, { useState } from 'react';
import AcademicLicensePopUp from '../SignUp/AcademicLicensePage';
import { XMarkIcon } from '@heroicons/react/24/outline';
import SharedButton from '@/global/Button/Button';

/**
 * Reusable modal for academic plan application
 * @param {boolean} isOpen - Whether modal is open
 * @param {function} onClose - Handler for closing modal
 * @param {function} onSubmit - Handler for submitting application
 * @param {string} academicStatus - Current academic status ('pending', 'approved', null)
 */
const AcademicApplicationModal = ({ isOpen, onClose, onSubmit, academicStatus }) => {
  const [showAcademicLicensePopup, setShowAcademicLicensePopup] = useState(false);
  const [academicLicenseAccepted, setAcademicLicenseAccepted] = useState(false);
  const [formErrorMessage, setFormErrorMessage] = useState('');

  const validateSubmit = () => {
    if (!academicLicenseAccepted) {
      setFormErrorMessage('You must accept the Disgenet Academic License before submitting.');
      return false;
    }
    setFormErrorMessage('');
    return true;
  };

  const handleAcademicLicenseCheckboxChange = e => {
    setAcademicLicenseAccepted(e.target.checked);
    if (e.target.checked) {
      setFormErrorMessage('');
    }
  };

  const handleAcademicLicensePopup = () => {
    setShowAcademicLicensePopup(true);
  };

  const handleSubmit = e => {
    e.preventDefault();

    if (!validateSubmit()) {
      return;
    }

    onClose();

    setTimeout(async () => {
      await onSubmit();
    }, 100);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-overlay">
        <div className="RegisterPopup">
          <div className="apply-popup-container">
            <button onClick={onClose} className="modal-close-btn">
              <XMarkIcon className="h-5 w-5 text-black" />  
            </button>

            {academicStatus === 'approved' ? (
              <>
                <h4>Your academic application has been approved!</h4>
                <p>Complete your payment to activate your Academic plan.</p>
                <SharedButton variant="gradient" onClick={handleSubmit} aria-label="submit-modal">
                  Proceed to Payment
                </SharedButton>
                <SharedButton variant="secondary" onClick={onClose} aria-label="cancel-modal">
                  Cancel
                </SharedButton>
              </>
            ) : (
              <>
                <h4>Submit your application for the Free Academic Plan</h4>
                <p>Your application will be reviewed before approval.</p>

                <div className="form-check mb-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={academicLicenseAccepted}
                    onChange={handleAcademicLicenseCheckboxChange}
                    id="academicLicenseCheckbox"
                  />
                  <label htmlFor="academicLicenseCheckbox">
                    I accept the{' '}
                    <button
                      type="button"
                      onClick={handleAcademicLicensePopup}
                      style={{ borderBottom: '2px solid rgb(192,195,202)' }}
                    >
                      DISGENET Academic License
                    </button>
                  </label>
                </div>

                <SharedButton variant="gradient" size="sm" onClick={handleSubmit} aria-label="submit-modal">
                  Submit
                </SharedButton>

                <SharedButton variant="secondary" size="sm" onClick={onClose} aria-label="cancel-modal">
                  Cancel
                </SharedButton>
                {formErrorMessage && <p className="form-error mt-4">{formErrorMessage}</p>}

                <p className="text-muted mt-3" style={{ fontSize: '0.85em' }}>
                  Not academic?{' '}
                  <a href="/Support" style={{ color: '#667eea', textDecoration: 'underline' }}>
                    Contact us
                  </a>
                  .
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      {showAcademicLicensePopup && (
        <AcademicLicensePopUp
          onClose={() => setShowAcademicLicensePopup(false)}
          onAcceptTerms={() => setAcademicLicenseAccepted(true)}
        />
      )}
    </>
  );
};

export default AcademicApplicationModal;
